import React, { useState, useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import Strapi from '../../services/strapi';
import { Evenement } from '../../interfaces/article';
import Keywords from '../../atoms/Keywords';
import ReadExtern, { ReadExternItem } from '../../atoms/ReadExtern';
import Authors, { sortAuthors } from '../../atoms/Authors';
import EvenementPicker from '../../atoms/EvenementPicker';
import ArticleEditorProps from '../../interfaces/ArticleEditorProps';
import ReadIntern, { article_to_readintern, readintern_to_article } from '../../atoms/ReadIntern';
import Paywall from '../../atoms/Paywall';
import Title from '../../atoms/Title';
import Surtitle from '../../atoms/SurTitle';
import MetaTitle from '../../atoms/MetaTitle';
import UrlTitle from '../../atoms/UrlTitle';
import ShortHat from '../../atoms/ShortHat';
import LongHat from '../../atoms/LongHat';
import Body from '../../atoms/Body';
import SeeInMostView from '../../atoms/SeeInMostView';
import MainTheme from '../../atoms/MainTheme';
import SecondaryThemes from '../../atoms/SecondaryThemes';
import Opinion from '../../atoms/Opinion';
import Image from '../../atoms/Image';
import Legend from '../../atoms/Legend';
import Source from '../../atoms/Source';
import SendToPartnerFeed from '../../atoms/SendToPartnerFeed';
import LinkToDossier from '../../atoms/LinkToDossier';
import RevisionMessage from '../../atoms/RevisionMessage';
import ArticleSnackbar from '../../atoms/ArticleSnackbar'
import { useParams } from 'react-router-dom';
import { Share, Notif } from '../../interfaces/article';
import SharePicker from '../../atoms/SharePicker';
import NotificationPicker from '../../atoms/NotificationPicker';

const PepiteEditor = (props: ArticleEditorProps) => {
  const { slug } = useParams<{ slug: string }>();
  const [id, setId] = useState<number>(-1);
  const [surtitle, setSurtitle] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [metatitle, setMetatitle] = useState<string>("");
  const [urltitle, setUrltitle] = useState<string>("");
  const [long_hat, setLong_hat] = useState<string>("");
  const [short_hat, setShort_hat] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [legend, setLegend] = useState<string>("");
  const [opinion, setOpinion] = useState<boolean>(false);
  const [see_in_most_view, setSee_in_most_view] = useState<boolean>(true);
  const [event, setEvent] = useState<Evenement>('Pasdevenement');
  const [theme, setTheme] = useState<any>(null);
  const [themes, setThemes] = useState<any[]>([]);
  const [mot_cles, setMot_cles] = useState<any[]>([]);
  const [paywall, setPaywall] = useState<string>("normal");
  const [readExtern, setReactExtern] = useState<ReadExternItem[]>([{ linktitle: "", linkurl: "", linktext: "" }]);
  const [authors, setAuthors] = useState<any[]>([]);
  const [image, setImage] = useState<any[]>([]);
  const [source, setSource] = useState<ReadExternItem>({ linktext: "", linktitle: "", linkurl: "" });
  const [readIntern, setReadIntern] = useState<any[]>([]);
  const [readInternBlocks, setReadInterBlocks] = useState<any[]>([]);
  const [send_to_partner_feed, setSend_to_partner_feed] = useState<boolean>(true);
  const [link_to_dossiers, setLink_to_dossiers] = useState<any[]>([]);
  const [message, setMessage] = useState<string>("");
  const [publish_date, setPublish_date] = useState<null | Date>(null);
  const [share, setShare] = useState<Share>(Share.Share);
  const [notif, setNotif] = useState<Notif>(Notif.Notif);
  const [comment, setComment] = useState<string>("");

  const update = () => {
    const article = {
      id,
      surtitle,
      title,
      urltitle,
      long_hat,
      short_hat,
      body,
      opinion,
      legend,
      metatitle,
      see_in_most_view,
      event,
      theme,
      themes,
      mot_cles,
      paywall,
      readExtern,
      authors,
      authors_order: authors.map(a => a.slug),
      image,
      source,
      readIntern: article_to_readintern(readIntern),
      readInternBlocks: article_to_readintern(readInternBlocks),
      send_to_partner_feed,
      link_to_dossiers,
      message,
      share: Share[share],
      notif: Notif[notif],
    };
    const url = props.type === "edit" ? "pepites/" + article.id + "/revision" : "pepites";
    return Strapi.post(url, {}, article);
  }

  useEffect(() => {
    if (props.type === "edit" && slug) {
      Strapi.get(`/pepites/${slug}`).then((r) => {
        setId(r.data.id);
        setSurtitle(r.data.surtitle ?? "");
        setTitle(r.data.title ?? "");
        setUrltitle(r.data.urltitle ?? "");
        setLong_hat(r.data.long_hat ?? "");
        setShort_hat(r.data.short_hat ?? "");
        setBody(r.data.body ?? "");
        setOpinion(r.data.optinion ?? false);
        setLegend(r.data.legend ?? "");
        setMetatitle(r.data.metatitle ?? "");
        setSee_in_most_view(r.data.see_in_most_view ?? true);
        setEvent(r.data.event ?? "Pasdevenement");
        setTheme(r.data.theme ?? "");
        setThemes(r.data.themes ?? "");
        setMot_cles(r.data.mot_cles ?? "");
        setReactExtern(r.data.readExtern ?? []);
        setReadIntern(readintern_to_article(r.data.readIntern));
        setReadInterBlocks(readintern_to_article(r.data.readInternBlocks));
        setAuthors(sortAuthors(r.data.authors ?? [], r.data.authors_order || []));
        setImage(r.data.image ?? []);
        setSource(r.data.source);
        setPaywall(r.data.paywall);
        setSend_to_partner_feed(r.data.send_to_partner_feed ?? false);
        setShare(Share[r.data.share as keyof typeof Share] ?? Share.Share);
        setNotif(Notif[r.data.notif as keyof typeof Notif] ?? Notif.NoNotif);
      });
    }
  }, [props.type, slug]);

  const isValid = useMemo(() => {
    if (!title) {
      setComment('Champ "Titre" manquant');
      return false;
    }
    if (!short_hat) {
      setComment('Champ "Chapeau court" manquant');
      return false;
    }
    if (!legend) {
      setComment('Champ "Legend" manquant');
      return false;
    }
    if (!theme) {
      setComment('Champ "Theme" manquant');
      return false;
    }
    if (mot_cles.length < 3) {
      setComment('Champ "Mot-clés" doit avoir 3 valeurs');
      return false;
    }
    setComment('');
    return true;
  }, [title, short_hat, legend, theme, mot_cles]);

  return (
    <div>
      <Grid container spacing={2} style={{flexGrow: 1, marginBottom: 0}}>
        <ArticleSnackbar publish_date={publish_date} setPublish_date={setPublish_date} disabled={!isValid} edtion_type={props.type} update={update} comment={comment} type="pepite"/>
        <Grid item xs={12}>
          <Title value={title} onChange={setTitle}/>
        </Grid>

        <Grid item xs={4}>
          <Surtitle value={surtitle} onChange={setSurtitle}/>
        </Grid>
        <Grid item xs={4}>
          <MetaTitle value={metatitle} onChange={setMetatitle}/>
        </Grid>
        <Grid item xs={4}>
          <UrlTitle type={props.type} value={urltitle} onChange={setUrltitle}/>
        </Grid>
        <Grid item xs={6}>
          <ShortHat value={short_hat} onChange={setShort_hat}/>
        </Grid>
        <Grid item xs={6}>
          <LongHat value={long_hat} onChange={setLong_hat}/>
        </Grid>
        <Body value={body} onChange={setBody}/>
        <Image value={image} onChange={setImage}/>
        <Legend value={legend} onChange={setLegend}/>
        <Source value={source} onChange={setSource}/>
        <Grid item xs={6}>
          <MainTheme value={theme} onChange={setTheme}/>
        </Grid>
        <Grid item xs={6}>
          <SecondaryThemes value={themes} onChange={setThemes}/>
        </Grid>
        <Authors value={authors} onChange={setAuthors} />
        <Grid item xs={props.type === "create" ? 6 : 12}>
          <Keywords value={mot_cles} onChange={setMot_cles} />
        </Grid>
        {props.type === "create" && <Grid item xs={6}>
          <LinkToDossier value={link_to_dossiers} onChange={setLink_to_dossiers}/>
        </Grid>}
        <ReadIntern value={readInternBlocks} onChange={setReadInterBlocks} label="à lire aussi" mot_cle={mot_cles}/>
        <ReadIntern value={readIntern} onChange={setReadIntern}/>
        <ReadExtern value={readExtern} onChange={setReactExtern}/>
        <Grid item xs={6} lg={3}>
          <EvenementPicker value={event} onChange={setEvent} />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Paywall value={paywall} onChange={setPaywall}/>
        </Grid>
        <Grid item xs={4} lg={2}>
          <Opinion value={opinion} onChange={setOpinion}/>
        </Grid>
        <Grid item xs={4} lg={2}>
          <SeeInMostView value={see_in_most_view} onChange={setSee_in_most_view}/>
        </Grid>
        <Grid item xs={4} lg={2}>
          <SendToPartnerFeed value={send_to_partner_feed} onChange={setSend_to_partner_feed}/>
        </Grid>
        <Grid item xs={6} lg={3}>
          <SharePicker value={share} onChange={setShare}/>
        </Grid>
        <Grid item xs={6} lg={3}>
          <NotificationPicker value={notif} articleType="pepite" article={{title: title, slug: slug, short_hat: short_hat}} onChange={setNotif}/>
        </Grid>
      </Grid>
      <RevisionMessage value={message} onChange={setMessage}/>
    </div>
  );
}

export default PepiteEditor;